// ============================================================
// 📁 آدرس این فایل در پروژه:
//    src/pages/customers/CustomerList.tsx
// 
// تغییرات این نسخه:
// - 🆕 در فرم ویرایش، بیلانس اولیه به‌صورت یک فیلد است که
//   عدد مثبت (قرضدار) و منفی (طلبکار) را قبول می‌کند
//   مثل فرم ثبت AddCustomer
// 
// هیچ بخش دیگری دست نخورده است
// ============================================================

import { useState, useEffect } from "react";
import { useTheme, DARK, LIGHT } from "../../components/Layout";
import { customersAPI } from "../../services/api";
import { Search, Eye, Trash2, Filter, ChevronDown, ChevronRight, Edit2, X, FileText } from "lucide-react";
import SearchableSelect from "../../components/SearchableSelect";
import CustomerReport from "./CustomerReport";

interface Props { onSelect: (id: number) => void; initialSearch?: string; }
const fmt = (n: any) => new Intl.NumberFormat("en-US").format(Math.abs(Number(n) || 0));

const CURRENCIES = ["USD", "AFN", "IRR", "TRY", "PKR", "EUR"];

function BalanceBadge({ val, currency, T }: { val: any; currency: string; T: any }) {
  const n = Number(val);
  const color = n > 0 ? "#ef4444" : n < 0 ? "#10b981" : T.subtext;
  const label = n > 0 ? "قرضدار" : n < 0 ? "طلبکار" : "تسویه";
  return (
    <div style={{ textAlign: "left" }}>
      <div style={{ fontSize: 13, fontWeight: 800, color }}>{n > 0 ? "+" : n < 0 ? "-" : ""}{fmt(n)} {currency}</div>
      <div style={{ fontSize: 10, color, opacity: 0.8 }}>{label}</div>
    </div>
  );
}

interface RowProps {
  c: any; indent?: boolean; T: any; dark: boolean; expanded: Set<number>;
  subs: Record<number, any[]>; toggleExpand: (id: number) => void;
  onSelect: (id: number) => void; handleDelete: (id: number, name: string) => void;
  onEdit: (c: any) => void; onReport: (c: any) => void;
}
function Row({ c, indent=false, T, dark, expanded, subs, toggleExpand, onSelect, handleDelete, onEdit, onReport }: RowProps) {
  return (
    <div>
      <div
        style={{ display: "grid", gridTemplateColumns: "32px 80px 1fr 130px 130px 150px 110px", gap: 10, padding: `12px ${indent ? "32px" : "18px"} 12px 18px`, borderBottom: `1px solid ${T.cardBorder}`, alignItems: "center", cursor: "pointer", transition: "background 0.15s", background: indent ? (dark ? "rgba(255,255,255,0.01)" : "rgba(0,0,0,0.01)") : "" }}
        onMouseEnter={e => (e.currentTarget.style.background = dark ? "rgba(139,92,246,0.04)" : "rgba(139,92,246,0.03)")}
        onMouseLeave={e => (e.currentTarget.style.background = indent ? (dark ? "rgba(255,255,255,0.01)" : "rgba(0,0,0,0.01)") : "")}
        onClick={() => onSelect(c.id)}
      >
        <div onClick={e => { e.stopPropagation(); if (c.sub_count > 0) toggleExpand(c.id); }} style={{ display: "flex", alignItems: "center", justifyContent: "center", cursor: c.sub_count > 0 ? "pointer" : "default" }}>
          {c.sub_count > 0 ? (expanded.has(c.id) ? <ChevronDown size={14} color={T.subtext} /> : <ChevronRight size={14} color={T.subtext} />) : <span style={{ width: 14 }} />}
        </div>
        <span style={{ fontSize: 11, fontFamily: "monospace", color: "#8b5cf6", fontWeight: 700 }}>{c.code}</span>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 9, background: indent ? "linear-gradient(135deg,#64748b,#475569)" : "linear-gradient(135deg,#8b5cf6,#6d28d9)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "white", fontWeight: 700, flexShrink: 0 }}>
            {c.first_name?.[0]}{c.last_name?.[0] || ""}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.text }}>{c.first_name} {c.last_name}</div>
            <div style={{ fontSize: 10, color: T.subtext }}>
              {c.company_name && `${c.company_name} • `}
              {c.sub_count > 0 && `${c.sub_count} زیرمجموعه • `}
              {c.tx_count} معامله
            </div>
          </div>
        </div>
        <span style={{ fontSize: 12, color: T.text }}>{c.phone || "—"}</span>
        <span style={{ fontSize: 12, color: T.subtext }}>{c.city || "—"}</span>
        <BalanceBadge val={c.current_balance} currency={c.currency} T={T} />
        <div style={{ display: "flex", gap: 5 }} onClick={e => e.stopPropagation()}>
          <button onClick={() => onSelect(c.id)} title="مشاهده" style={{ width: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer", background: "rgba(139,92,246,0.12)", color: "#8b5cf6", display: "flex", alignItems: "center", justifyContent: "center" }}><Eye size={12} /></button>
          <button onClick={() => onReport(c)} title="گزارش" style={{ width: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer", background: "rgba(16,185,129,0.12)", color: "#10b981", display: "flex", alignItems: "center", justifyContent: "center" }}><FileText size={12} /></button>
          <button onClick={() => onEdit(c)} title="ویرایش" style={{ width: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer", background: "rgba(59,130,246,0.12)", color: "#3b82f6", display: "flex", alignItems: "center", justifyContent: "center" }}><Edit2 size={12} /></button>
          <button onClick={() => handleDelete(c.id, `${c.first_name} ${c.last_name}`)} title="حذف" style={{ width: 28, height: 28, borderRadius: 7, border: "none", cursor: "pointer", background: "rgba(239,68,68,0.12)", color: "#ef4444", display: "flex", alignItems: "center", justifyContent: "center" }}><Trash2 size={12} /></button>
        </div>
      </div>
      {expanded.has(c.id) && (subs[c.id] || []).map((sub: any) => (
        <Row key={sub.id} c={sub} indent T={T} dark={dark} expanded={expanded} subs={subs} toggleExpand={toggleExpand} onSelect={onSelect} handleDelete={handleDelete} onEdit={onEdit} onReport={onReport} />
      ))}
    </div>
  );
}

export default function CustomerList({ onSelect, initialSearch = "" }: Props) {
  const { dark } = useTheme();
  const T = dark ? DARK : LIGHT;
  const [customers, setCustomers] = useState<any[]>([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState(initialSearch);
  const [status, setStatus]       = useState("");
  const [toast, setToast]         = useState("");
  const [expanded, setExpanded]   = useState<Set<number>>(new Set());
  const [subs, setSubs]           = useState<Record<number, any[]>>({});
  const [editItem, setEditItem]   = useState<any>(null);
  const [saving, setSaving]       = useState(false);
  const [reportItem, setReportItem] = useState<any>(null);

  const showToast = (msg: string) => { setToast(msg); setTimeout(() => setToast(""), 3000); };

  const load = async () => {
    setLoading(true);
    try {
      const r = await customersAPI.list({ search, status, parent_only: !search });
      if (r.success) setCustomers(r.customers);
    } catch { showToast("❌ خطا در اتصال"); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [search, status]);
  useEffect(() => { setSearch(initialSearch); }, [initialSearch]);

  const toggleExpand = async (id: number) => {
    const next = new Set(expanded);
    if (next.has(id)) { next.delete(id); }
    else {
      next.add(id);
      if (!subs[id]) {
        const r = await customersAPI.list({ parent_id: id });
        if (r.success) setSubs(s => ({ ...s, [id]: r.customers }));
      }
    }
    setExpanded(next);
  };

  const handleDelete = async (id: number, name: string) => {
    if (!window.confirm(`"${name}" حذف شود؟`)) return;
    const r = await customersAPI.delete(id);
    if (r.success) { showToast("✅ حذف شد"); load(); }
    else showToast("❌ " + r.message);
  };

  // 🆕 در ویرایش: بیلانس اولیه را با علامت (مثبت/منفی) به state می‌بریم
  // مثبت = قرضدار (debit) — منفی = طلبکار (credit)
  const handleEdit = (c: any) => {
    const ob = parseFloat(c.opening_balance || 0);
    const obType = c.opening_balance_type || "debit";
    const signedBalance = obType === "credit" ? -Math.abs(ob) : Math.abs(ob);
    setEditItem({
      id: c.id,
      first_name: c.first_name || "",
      last_name: c.last_name || "",
      company_name: c.company_name || "",
      phone: c.phone || "",
      phone2: c.phone2 || "",
      address: c.address || "",
      city: c.city || "",
      country: c.country || "",
      currency: c.currency || "USD",
      // 🆕 یک فیلد با علامت — کاربر می‌تواند مستقیم منفی بزند
      opening_balance: String(signedBalance),
      notes: c.notes || "",
      status: c.status || "active",
    });
  };

  // 🆕 در ذخیره: علامت را به abs و type تبدیل می‌کنیم
  const handleSave = async () => {
    if (!editItem) return;
    setSaving(true);
    const balVal = parseFloat(editItem.opening_balance || "0");
    const payload = {
      ...editItem,
      opening_balance: Math.abs(balVal),
      opening_balance_type: balVal >= 0 ? "debit" : "credit",
    };
    const r = await customersAPI.update(payload);
    setSaving(false);
    if (r.success) {
      showToast("✅ مشتری ویرایش شد");
      setEditItem(null);
      load();
    } else showToast("❌ " + r.message);
  };

  const inp: React.CSSProperties = { padding: "9px 14px", background: dark ? "rgba(255,255,255,0.05)" : "#f8faff", border: `1.5px solid ${dark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.08)"}`, borderRadius: 10, color: T.text, fontSize: 13, fontFamily: "'Vazirmatn','Segoe UI',sans-serif", direction: "rtl" as const, outline: "none", width: "100%", boxSizing: "border-box" as const };
  const statusOptions = [{ value: "active", label: "فعال" }, { value: "inactive", label: "غیرفعال" }];

  // 🆕 محاسبه برای نمایش نشانگر زیر فیلد بیلانس
  const editBalVal = editItem ? parseFloat(editItem.opening_balance || "0") : 0;
  const editBalColor = editBalVal > 0 ? "#ef4444" : editBalVal < 0 ? "#10b981" : "#64748b";
  const editBalLabel = editBalVal > 0
    ? `🔴 مشتری ${fmt(editBalVal)} ${editItem?.currency || ""} به ما قرضدار است`
    : editBalVal < 0
    ? `🟢 ما ${fmt(editBalVal)} ${editItem?.currency || ""} به مشتری قرضداریم`
    : "✅ بیلانس صفر — حساب تسویه";

  return (
    <div dir="rtl">
      {toast && <div style={{ position: "fixed", top: 20, left: "50%", transform: "translateX(-50%)", zIndex: 999, padding: "12px 24px", borderRadius: 12, background: dark ? "#1e293b" : "#fff", boxShadow: "0 8px 32px rgba(0,0,0,0.3)", color: T.text, fontSize: 13, fontWeight: 600, fontFamily: "inherit" }}>{toast}</div>}

      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ flex: 1, minWidth: 200, position: "relative" }}>
          <Search size={14} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", color: T.subtext }} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="جستجو در نام، کد، شماره تماس..." style={{ ...inp, paddingRight: 36 }} />
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Filter size={14} color={T.subtext} />
          <div style={{ width: 160 }}>
            <SearchableSelect options={statusOptions} value={status} onChange={v => setStatus(v)} placeholder="همه وضعیت‌ها"/>
          </div>
        </div>
      </div>

      <div style={{ borderRadius: 16, overflow: "hidden", border: `1px solid ${T.cardBorder}`, background: dark ? "rgba(255,255,255,0.02)" : "#fff" }}>
        <div style={{ display: "grid", gridTemplateColumns: "32px 80px 1fr 130px 130px 150px 110px", gap: 10, padding: "12px 18px", background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)", borderBottom: `1px solid ${T.cardBorder}`, fontSize: 11, fontWeight: 700, color: T.subtext }}>
          <span /><span>کد</span><span>مشتری</span><span>تماس</span><span>شهر</span><span>بیلانس</span><span>عملیات</span>
        </div>
        {loading ? (
          <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>
            <div style={{ width: 32, height: 32, border: "3px solid rgba(139,92,246,0.2)", borderTop: "3px solid #8b5cf6", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto 12px" }} />
            در حال بارگذاری...
          </div>
        ) : customers.length === 0 ? (
          <div style={{ textAlign: "center", padding: 60, color: T.subtext }}>
            <div style={{ fontSize: 40, marginBottom: 10 }}>🤝</div>
            <div>هیچ مشتری‌ای پیدا نشد</div>
          </div>
        ) : customers.map(c => (
          <Row key={c.id} c={c} T={T} dark={dark} expanded={expanded} subs={subs} toggleExpand={toggleExpand} onSelect={onSelect} handleDelete={handleDelete} onEdit={handleEdit} onReport={setReportItem} />
        ))}
      </div>

      {/* Modal ویرایش */}
      {editItem && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20 }}
          onClick={e => { if (e.target === e.currentTarget) setEditItem(null); }}>
          <div style={{ background: dark ? "#1e293b" : "#fff", borderRadius: 20, padding: 28, width: "100%", maxWidth: 560, maxHeight: "90vh", overflow: "auto", boxShadow: "0 25px 60px rgba(0,0,0,0.4)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: T.text }}>ویرایش مشتری</h3>
              <button onClick={() => setEditItem(null)} style={{ background: "none", border: "none", cursor: "pointer", color: T.subtext }}><X size={20} /></button>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>نام</label>
                <input value={editItem.first_name} onChange={e => setEditItem((f: any) => ({ ...f, first_name: e.target.value }))} style={inp} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>تخلص</label>
                <input value={editItem.last_name} onChange={e => setEditItem((f: any) => ({ ...f, last_name: e.target.value }))} style={inp} />
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>نام شرکت</label>
                <input value={editItem.company_name} onChange={e => setEditItem((f: any) => ({ ...f, company_name: e.target.value }))} style={inp} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>تماس ۱</label>
                <input value={editItem.phone} onChange={e => setEditItem((f: any) => ({ ...f, phone: e.target.value }))} style={inp} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>تماس ۲</label>
                <input value={editItem.phone2} onChange={e => setEditItem((f: any) => ({ ...f, phone2: e.target.value }))} style={inp} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>شهر</label>
                <input value={editItem.city} onChange={e => setEditItem((f: any) => ({ ...f, city: e.target.value }))} style={inp} />
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>کشور</label>
                <input value={editItem.country} onChange={e => setEditItem((f: any) => ({ ...f, country: e.target.value }))} style={inp} />
              </div>
              <div style={{ gridColumn: "1/-1" }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>آدرس</label>
                <input value={editItem.address} onChange={e => setEditItem((f: any) => ({ ...f, address: e.target.value }))} style={inp} />
              </div>
              {/* 🆕 بیلانس اولیه — یک فیلد با مثبت/منفی */}
              <div style={{ gridColumn: "1/-1", background: dark ? "rgba(59,130,246,0.08)" : "#eff6ff", borderRadius: 12, padding: "14px 16px", border: "1.5px solid #93c5fd" }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: "#3b82f6", marginBottom: 6 }}>💰 بیلانس اولیه</div>
                <div style={{ fontSize: 11, color: T.subtext, marginBottom: 12 }}>
                  عدد <strong style={{ color: "#ef4444" }}>مثبت</strong> = مشتری به ما قرضدار است &nbsp;|&nbsp;
                  عدد <strong style={{ color: "#10b981" }}>منفی</strong> = ما به مشتری قرضداریم
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 10 }}>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>مبلغ (مثبت یا منفی)</label>
                    <input
                      type="number"
                      value={editItem.opening_balance}
                      onChange={e => setEditItem((f: any) => ({ ...f, opening_balance: e.target.value }))}
                      placeholder="مثال: 5000 یا -3000"
                      style={{ ...inp, fontSize: 15, fontWeight: 700, color: editBalColor }}
                    />
                  </div>
                  <div>
                    <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>ارز</label>
                    <select value={editItem.currency} onChange={e => setEditItem((f: any) => ({ ...f, currency: e.target.value }))} style={{ ...inp }}>
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                </div>
                {editItem.opening_balance !== "" && editItem.opening_balance !== "0" && (
                  <div style={{ marginTop: 10, padding: "8px 14px", borderRadius: 8, background: editBalVal > 0 ? "rgba(239,68,68,0.06)" : editBalVal < 0 ? "rgba(16,185,129,0.06)" : "rgba(148,163,184,0.06)", border: `1px solid ${editBalColor}30`, fontSize: 12, fontWeight: 700, color: editBalColor }}>
                    {editBalLabel}
                  </div>
                )}
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>وضعیت</label>
                <select value={editItem.status} onChange={e => setEditItem((f: any) => ({ ...f, status: e.target.value }))} style={{ ...inp }}>
                  <option value="active">فعال</option>
                  <option value="inactive">غیرفعال</option>
                </select>
              </div>
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: T.subtext, display: "block", marginBottom: 5 }}>یادداشت</label>
                <input value={editItem.notes} onChange={e => setEditItem((f: any) => ({ ...f, notes: e.target.value }))} style={inp} />
              </div>
            </div>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 20 }}>
              <button onClick={() => setEditItem(null)} style={{ padding: "10px 20px", borderRadius: 10, border: `1.5px solid ${T.cardBorder}`, background: "transparent", color: T.text, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>انصراف</button>
              <button onClick={handleSave} disabled={saving} style={{ padding: "10px 24px", borderRadius: 10, border: "none", background: saving ? "#9ca3af" : "linear-gradient(135deg,#3b82f6,#2563eb)", color: "#fff", cursor: saving ? "not-allowed" : "pointer", fontSize: 13, fontWeight: 700, fontFamily: "'Vazirmatn','Segoe UI',sans-serif" }}>
                {saving ? "در حال ذخیره..." : "ذخیره ویرایش"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Modal گزارش */}
      {reportItem && (
        <CustomerReport
          entity={reportItem}
          entityType="customer"
          onClose={() => setReportItem(null)}
          T={T}
          dark={dark}
        />
      )}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}